import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import Button from '../components/Button';
import { Input } from '../components/FormField';

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function EventCatering({ eventId, hasPaidTicket }) {
  const [caterings, setCaterings] = useState(null);
  const [numPeople, setNumPeople] = useState({});
  const [busy, setBusy] = useState({});
  const [error, setError] = useState({});
  const [message, setMessage] = useState({});

  const load = useCallback(() => {
    api.get(`/api/events/${eventId}/catering`).then((r) => setCaterings(r.data)).catch(() => setCaterings([]));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const order = async (c) => {
    setBusy((p) => ({ ...p, [c.id]: true }));
    setError((p) => ({ ...p, [c.id]: '' }));
    setMessage((p) => ({ ...p, [c.id]: '' }));
    try {
      await api.post(`/api/events/${eventId}/catering/${c.id}/order`, { num_people: numPeople[c.id] || 1 });
      setMessage((p) => ({ ...p, [c.id]: 'Anmeldung erfolgreich! Du bekommst eine E-Mail mit den Zahlungsdaten.' }));
      load();
    } catch (err) {
      setError((p) => ({ ...p, [c.id]: err.response?.data?.detail || 'Fehler bei der Anmeldung' }));
    } finally { setBusy((p) => ({ ...p, [c.id]: false })); }
  };

  const cancel = async (c) => {
    if (!window.confirm('Anmeldung wirklich stornieren?')) return;
    setBusy((p) => ({ ...p, [c.id]: true }));
    setError((p) => ({ ...p, [c.id]: '' }));
    try {
      await api.delete(`/api/events/${eventId}/catering/${c.id}/order`);
      load();
    } catch (err) {
      setError((p) => ({ ...p, [c.id]: err.response?.data?.detail || 'Fehler beim Stornieren' }));
    } finally { setBusy((p) => ({ ...p, [c.id]: false })); }
  };

  if (caterings === null) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-2 border-ocean-400 border-t-transparent" /></div>;
  }

  if (caterings.length === 0) {
    return <div className="card text-center py-10 text-slate-400 text-sm">Aktuell ist kein Catering für dieses Event geplant.</div>;
  }

  return (
    <div className="space-y-4">
      {caterings.map((c) => {
        const price = parseFloat(c.price_per_person);
        const people = numPeople[c.id] || 1;
        return (
          <div key={c.id} className="card">
            <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
              <div>
                <p className="font-bold text-ocean-900 text-lg">🐷 {c.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{fmt(c.date_time)}</p>
                {c.order_deadline && (
                  <p className="text-xs text-slate-500 mt-0.5">Anmeldeschluss: {fmt(c.order_deadline)}</p>
                )}
              </div>
              <span className="font-bold text-sand-700">{price.toFixed(2)} € p.P.</span>
            </div>

            {c.description && (
              <p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">{c.description}</p>
            )}

            {c.progress_percent !== null && c.progress_percent !== undefined && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Anmeldungen</span>
                  <span className="font-semibold">{c.progress_percent}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${c.progress_percent >= 100 ? 'bg-emerald-500' : 'bg-ocean-500'}`}
                    style={{ width: `${c.progress_percent}%` }}
                  />
                </div>
              </div>
            )}

            {message[c.id] && <div className="alert-success mb-3 text-sm">{message[c.id]}</div>}
            {error[c.id] && <div className="alert-error mb-3 text-sm">{error[c.id]}</div>}

            {!hasPaidTicket ? (
              <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
                Du benötigst ein bezahltes Ticket für dieses Event, um dich zum Catering anzumelden.
              </p>
            ) : !c.my_order && c.is_locked ? (
              <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
                Die Anmeldung ist geschlossen.
              </p>
            ) : c.my_order ? (
              <div className="border-t border-slate-100 pt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="font-medium text-slate-700">Angemeldet für {c.my_order.num_people} Person{c.my_order.num_people !== 1 ? 'en' : ''}</span>
                  {' · '}
                  <span className="font-semibold text-sand-700">{(price * c.my_order.num_people).toFixed(2)} €</span>
                  {' '}
                  {c.my_order.paid
                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1">Bezahlt</span>
                    : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 ml-1">Zahlung ausstehend</span>
                  }
                </div>
                {!c.my_order.paid && (
                  <Button variant="danger" size="sm" loading={busy[c.id]} onClick={() => cancel(c)}>Stornieren</Button>
                )}
              </div>
            ) : (
              <div className="border-t border-slate-100 pt-3 flex flex-wrap items-end gap-3">
                <div>
                  <label className="label mb-1 block">Personen</label>
                  <Input type="number" min="1" value={people} className="w-20"
                    onChange={(e) => setNumPeople((p) => ({ ...p, [c.id]: Math.max(1, parseInt(e.target.value) || 1) }))} />
                </div>
                <span className="text-sm text-slate-500 pb-2">= {(price * people).toFixed(2)} €</span>
                <Button size="sm" loading={busy[c.id]} onClick={() => order(c)} className="ml-auto">Jetzt anmelden</Button>
              </div>
            )}

            {c.my_order && c.paypal_link && !c.my_order.paid && (
              <div className="mt-3 bg-ocean-50 border border-ocean-100 rounded px-3 py-2 text-xs text-slate-600">
                <a href={c.paypal_link} target="_blank" rel="noreferrer" className="text-ocean-600 hover:underline break-all">{c.paypal_link}</a>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
